import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/layout/Layout';
import { useLanguage } from '../contexts/LanguageContext';
import { getInvoices, createInvoice, updateInvoice, getClients, getProjects, getCompanyInfo } from '../lib/api';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  ActionButton,
  AppDialog,
  DataTable,
  MetricCard,
  PageToolbar,
  StatsGrid,
  StatusBadge,
} from '../components/app';
import {
  InvoiceCreateForm,
  InvoiceDetailDialog,
  InvoiceEditDialog,
  createInvoiceColumns,
} from '../components/invoices';
import {
  Plus,
  MagnifyingGlass,
  Receipt,
  Eye,
  Check,
  X,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import html2pdf from 'html2pdf.js';



const PAGE_SIZE = 20;

export function InvoicesPage() {
  const { t } = useLanguage();
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [monthFilter, setMonthFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [showAllRecords, setShowAllRecords] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [viewingInvoice, setViewingInvoice] = useState(null);
  const [cufeDialogOpen, setCufeDialogOpen] = useState(false);
  const [pendingIssueRequest, setPendingIssueRequest] = useState(null);
  const [cufeValue, setCufeValue] = useState('');
  const [cufeError, setCufeError] = useState('');
  const [cufeSubmitting, setCufeSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    client_id: '',
    project_id: '',
    items: [{ description: '', quantity: 1, unit_price: 0, total: 0 }],
    subtotal: 0,
    tax_rate: 0,
    tax_amount: 0,
    total: 0,
    issue_date: new Date().toISOString().split('T')[0],
    due_date: '',
    notes: '',
  });
  const [editFormData, setEditFormData] = useState({
    client_id: '',
    project_id: '',
    items: [],
    subtotal: 0,
    tax_rate: 0,
    tax_amount: 0,
    total: 0,
    issue_date: '',
    due_date: '',
    notes: '',
    status: '',
    paid_date: '',
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (monthFilter) params.month = monthFilter;
      if (searchTerm.trim()) params.search = searchTerm.trim();
      if (showAllRecords) {
        params.limit = 0;
      } else {
        params.skip = (currentPage - 1) * PAGE_SIZE;
        params.limit = PAGE_SIZE + 1;
      }
      const [invoicesRes, clientsRes, projectsRes, companyRes] = await Promise.all([
        getInvoices(params),
        getClients(),
        getProjects(),
        getCompanyInfo().catch(() => ({ data: null })),
      ]);
      const invoiceItems = Array.isArray(invoicesRes.data) ? invoicesRes.data : [];
      setHasNextPage(!showAllRecords && invoiceItems.length > PAGE_SIZE);
      setInvoices(showAllRecords ? invoiceItems : invoiceItems.slice(0, PAGE_SIZE));
      setClients(clientsRes.data);
      setProjects(projectsRes.data);
      setCompanyInfo(companyRes.data);
    } catch (error) {
      toast.error(t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  }, [currentPage, monthFilter, searchTerm, showAllRecords, statusFilter, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setCurrentPage(1);
  }, [monthFilter, searchTerm, showAllRecords, statusFilter]);

  const calculateTotals = (items, taxRate) => {
    const subtotal = items.reduce((sum, item) => sum + (item.total || 0), 0);
    const tax_amount = subtotal * (taxRate / 100);
    const total = subtotal + tax_amount;
    return { subtotal, tax_amount, total };
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    
    if (field === 'quantity' || field === 'unit_price') {
      newItems[index].total = (newItems[index].quantity || 0) * (newItems[index].unit_price || 0);
    }
    
    const totals = calculateTotals(newItems, formData.tax_rate);
    setFormData({ ...formData, items: newItems, ...totals });
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: '', quantity: 1, unit_price: 0, total: 0 }]
    });
  };

  const removeItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    const totals = calculateTotals(newItems, formData.tax_rate);
    setFormData({ ...formData, items: newItems, ...totals });
  };

  const handleTaxRateChange = (rate) => {
    const totals = calculateTotals(formData.items, rate);
    setFormData({ ...formData, tax_rate: rate, ...totals });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.client_id) {
      toast.error('Please select a client');
      return;
    }
    try {
      await createInvoice(formData);
      toast.success(t('successSaved'));
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await updateInvoice(id, { status, ...(status === 'pagada' ? { paid_date: new Date().toISOString().split('T')[0] } : {}) });
      toast.success(t('successSaved'));
      fetchData();
      setViewingInvoice(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const invoiceRequiresCufeToIssue = (invoice, payload = {}) => (
    invoice?.status === 'borrador' &&
    payload.status === 'emitida' &&
    Number(payload.tax_rate ?? invoice.tax_rate ?? 0) > 0 &&
    !(payload.cufe || invoice.cufe)
  );

  const openCufeDialog = (invoice, payload, options = {}) => {
    setPendingIssueRequest({ invoice, payload, ...options });
    setCufeValue('');
    setCufeError('');
    setCufeDialogOpen(true);
  };

  const submitInvoiceUpdate = async ({ invoice, payload, closeViewing = false, closeEditing = false }) => {
    await updateInvoice(invoice._id, payload);
    toast.success(t('successSaved'));
    fetchData();
    if (closeViewing) setViewingInvoice(null);
    if (closeEditing) {
      setEditDialogOpen(false);
      setEditingInvoice(null);
    }
  };

  const requestInvoiceIssue = async (invoice, payload = { status: 'emitida' }, options = {}) => {
    if (invoiceRequiresCufeToIssue(invoice, payload)) {
      openCufeDialog(invoice, payload, options);
      return;
    }

    try {
      await submitInvoiceUpdate({ invoice, payload, ...options });
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const confirmCufeIssue = async () => {
    const cufe = cufeValue.trim();
    if (!cufe) {
      setCufeError('El CUFE es requerido para emitir facturas con impuesto.');
      return;
    }
    if (!pendingIssueRequest) return;

    setCufeSubmitting(true);
    setCufeError('');
    try {
      await submitInvoiceUpdate({
        ...pendingIssueRequest,
        payload: { ...pendingIssueRequest.payload, cufe },
      });
      setCufeDialogOpen(false);
      setPendingIssueRequest(null);
      setCufeValue('');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    } finally {
      setCufeSubmitting(false);
    }
  };

  // Open edit dialog for an existing invoice
  const openEditDialog = (invoice) => {
    setEditingInvoice(invoice);
    setEditFormData({
      client_id: invoice.client_id || '',
      project_id: invoice.project_id || '',
      items: (invoice.items || []).map(item => ({ ...item })),
      subtotal: invoice.subtotal || 0,
      tax_rate: invoice.tax_rate || 0,
      tax_amount: invoice.tax_amount || 0,
      total: invoice.total || 0,
      issue_date: invoice.issue_date || '',
      due_date: invoice.due_date || '',
      notes: invoice.notes || '',
      status: invoice.status || '',
      paid_date: invoice.paid_date || '',
    });
    setEditDialogOpen(true);
  };

  // Whether the invoice being edited is in draft mode
  const isEditDraft = editingInvoice?.status === 'borrador';

  // Edit mode: update an item
  const updateEditItem = (index, field, value) => {
    const newItems = [...editFormData.items];
    newItems[index][field] = value;
    
    if (field === 'quantity' || field === 'unit_price') {
      newItems[index].total = (newItems[index].quantity || 0) * (newItems[index].unit_price || 0);
    }
    
    const totals = calculateTotals(newItems, editFormData.tax_rate);
    setEditFormData({ ...editFormData, items: newItems, ...totals });
  };

  // Edit mode: add/remove items
  const addEditItem = () => {
    setEditFormData({
      ...editFormData,
      items: [...editFormData.items, { description: '', quantity: 1, unit_price: 0, total: 0 }]
    });
  };

  const removeEditItem = (index) => {
    const newItems = editFormData.items.filter((_, i) => i !== index);
    const totals = calculateTotals(newItems, editFormData.tax_rate);
    setEditFormData({ ...editFormData, items: newItems, ...totals });
  };

  // Edit mode: tax rate change
  const handleEditTaxRateChange = (rate) => {
    const totals = calculateTotals(editFormData.items, rate);
    setEditFormData({ ...editFormData, tax_rate: rate, ...totals });
  };

  // Handle edit form submit
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingInvoice) return;

    const updatePayload = {};
    
    // Always-editable fields
    if (editFormData.status !== editingInvoice.status) updatePayload.status = editFormData.status;
    if (editFormData.notes !== editingInvoice.notes) updatePayload.notes = editFormData.notes;
    if (editFormData.paid_date !== (editingInvoice.paid_date || '')) updatePayload.paid_date = editFormData.paid_date;
    
    // Draft-only fields (only include if in draft)
    if (isEditDraft) {
      if (editFormData.client_id !== editingInvoice.client_id) updatePayload.client_id = editFormData.client_id;
      if (editFormData.project_id !== (editingInvoice.project_id || '')) updatePayload.project_id = editFormData.project_id;
      if (editFormData.issue_date !== editingInvoice.issue_date) updatePayload.issue_date = editFormData.issue_date;
      if (editFormData.due_date !== editingInvoice.due_date) updatePayload.due_date = editFormData.due_date;
      if (editFormData.tax_rate !== (editingInvoice.tax_rate || 0)) updatePayload.tax_rate = editFormData.tax_rate;
      if (editFormData.subtotal !== (editingInvoice.subtotal || 0)) updatePayload.subtotal = editFormData.subtotal;
      if (editFormData.tax_amount !== (editingInvoice.tax_amount || 0)) updatePayload.tax_amount = editFormData.tax_amount;
      if (editFormData.total !== (editingInvoice.total || 0)) updatePayload.total = editFormData.total;
      if (JSON.stringify(editFormData.items) !== JSON.stringify(editingInvoice.items || [])) updatePayload.items = editFormData.items;
    }

    if (Object.keys(updatePayload).length === 0) {
      toast.info('No changes detected');
      return;
    }

    if (invoiceRequiresCufeToIssue(editingInvoice, updatePayload)) {
      openCufeDialog(editingInvoice, updatePayload, { closeEditing: true });
      return;
    }

    try {
      await updateInvoice(editingInvoice._id, updatePayload);
      toast.success(t('successSaved'));
      setEditDialogOpen(false);
      setEditingInvoice(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const resetForm = () => {
    setFormData({
      client_id: '',
      project_id: '',
      items: [{ description: '', quantity: 1, unit_price: 0, total: 0 }],
      subtotal: 0,
      tax_rate: 0,
      tax_amount: 0,
      total: 0,
      issue_date: new Date().toISOString().split('T')[0],
      due_date: '',
      notes: '',
    });
  };

  const getClient = (clientId) => clients.find(c => c._id === clientId);
  const getClientName = (clientId) => {
    const client = getClient(clientId);
    return client?.name || 'Unknown';
  };
  const getClientRUC = (clientId) => {
    const client = getClient(clientId);
    return client?.tax_id || '';
  };

  const exportInvoicePdf = async () => {
    try {
      const element = document.getElementById('invoice-pdf');
      if (!element) {
        toast.error('No se encontró el contenido para exportar.');
        return;
      }
      if (document.fonts?.ready) {
        await document.fonts.ready;
      }
      await new Promise((resolve) => setTimeout(resolve, 150));
      const opt = {
        margin: 0,
        filename: `${viewingInvoice?.invoice_number || 'Factura'}.pdf`,
        image: { type: 'jpeg', quality: 1 },
        html2canvas: { scale: 3, useCORS: true, backgroundColor: '#ffffff', scrollX: 0, scrollY: 0, letterRendering: true },
        jsPDF: { unit: 'pt', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
      };
      await html2pdf().set(opt).from(element).save();
    } catch (error) {
      console.error(error);
      toast.error('No se pudo generar el PDF.');
    }
  };

  const totalInvoiced = invoices.filter(i => i.status === 'emitida').reduce((sum, i) => sum + (i.total || 0), 0);
  const totalPaid = invoices.filter(i => i.status === 'pagada').reduce((sum, i) => sum + (i.total || 0), 0);
  const totalOverdue = invoices.filter(i => i.status === 'vencida').reduce((sum, i) => sum + (i.total || 0), 0);

  const invoiceColumns = createInvoiceColumns({
    t,
    getClientName,
    getClientRUC,
    onViewInvoice: setViewingInvoice,
  });

  return (
    <Layout title={t('invoices')}>
      <div className="space-y-6" data-testid="invoices-page">
        {/* Stats */}
        <StatsGrid className="md:grid-cols-3 xl:grid-cols-3">
          <MetricCard label={t('issued')} value={`$${totalInvoiced.toLocaleString()}`} icon={Receipt} tone="info" />
          <MetricCard label={t('paid')} value={`$${totalPaid.toLocaleString()}`} icon={Check} tone="success" />
          <MetricCard label={t('overdue')} value={`$${totalOverdue.toLocaleString()}`} icon={X} tone="danger" />
        </StatsGrid>

        {/* Header */}
        <PageToolbar
          filters={
            <>
            <div className="relative flex-1 max-w-md">
              <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
              <Input
                placeholder="Buscar por factura, cotización, descripción o monto"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 valhalla-input"
                data-testid="invoice-search"
              />
            </div>
            <Input
              type="month"
              value={monthFilter}
              onChange={(e) => setMonthFilter(e.target.value)}
              className="w-full valhalla-input sm:w-40"
              aria-label="Filtrar facturas por mes"
            />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-36 valhalla-input">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#151B26] border-[#242E42]">
                <SelectItem value="all">{t('all')}</SelectItem>
                <SelectItem value="borrador">{t('draft')}</SelectItem>
                <SelectItem value="emitida">{t('issued')}</SelectItem>
                <SelectItem value="pagada">{t('paid')}</SelectItem>
                <SelectItem value="vencida">{t('overdue')}</SelectItem>
              </SelectContent>
            </Select>
            <ActionButton
              label={showAllRecords ? '20 por página' : 'Ver todo'}
              tone={showAllRecords ? 'warning' : 'info'}
              onClick={() => setShowAllRecords((value) => !value)}
            />
            </>
          }
          actions={
            <AppDialog
              open={isDialogOpen}
              onOpenChange={(open) => {
                setIsDialogOpen(open);
                if (!open) resetForm();
              }}
              size="lg"
              title={t('newInvoice')}
              trigger={
                <ActionButton
                  icon={Plus}
                  label={t('newInvoice')}
                  tone="primary"
                  className="font-semibold"
                  data-testid="new-invoice-btn"
                />
              }
            >
              <InvoiceCreateForm
                formData={formData}
                setFormData={setFormData}
                clients={clients}
                projects={projects}
                t={t}
                onSubmit={handleSubmit}
                onCancel={() => setIsDialogOpen(false)}
                onAddItem={addItem}
                onUpdateItem={updateItem}
                onRemoveItem={removeItem}
                onTaxRateChange={handleTaxRateChange}
              />
            </AppDialog>
          }
        />

        {/* Invoices List */}
        <DataTable
          columns={invoiceColumns}
          data={invoices}
          getRowKey={(invoice) => invoice._id}
          loading={loading}
          loadingLabel={t('loading')}
          emptyIcon={Receipt}
          emptyDescription={t('noData')}
          mobileTitle={(invoice) => invoice.invoice_number}
          mobileSubtitle={(invoice) =>
            invoice.quotation_number
              ? `${getClientName(invoice.client_id)} · ${invoice.quotation_number}`
              : getClientName(invoice.client_id)
          }
          mobileBadge={(invoice) => (
            <StatusBadge value={invoice.status} statusContext="invoice" />
          )}
          mobileActions={(invoice) => (
            <ActionButton
              icon={Eye}
              label="Ver"
              tone="view"
              onClick={() => setViewingInvoice(invoice)}
            />
          )}
        />

        {!showAllRecords ? (
          <div className="flex flex-col gap-3 rounded-lg border border-[#242E42] bg-[#151B26] p-3 text-sm text-[#9CA3AF] sm:flex-row sm:items-center sm:justify-between">
            <span>
              Mostrando hasta {PAGE_SIZE} facturas por página · Página {currentPage}
            </span>
            <div className="flex items-center gap-2">
              <ActionButton
                label="Anterior"
                tone="default"
                disabled={currentPage === 1 || loading}
                onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
              />
              <ActionButton
                label="Siguiente"
                tone="default"
                disabled={loading || !hasNextPage}
                onClick={() => setCurrentPage((page) => page + 1)}
              />
            </div>
          </div>
        ) : null}

        <InvoiceEditDialog
          open={editDialogOpen}
          onOpenChange={(open) => {
            setEditDialogOpen(open);
            if (!open) setEditingInvoice(null);
          }}
          editingInvoice={editingInvoice}
          formData={editFormData}
          setFormData={setEditFormData}
          clients={clients}
          projects={projects}
          t={t}
          isEditDraft={isEditDraft}
          onSubmit={handleEditSubmit}
          onCancel={() => {
            setEditDialogOpen(false);
            setEditingInvoice(null);
          }}
          onAddItem={addEditItem}
          onUpdateItem={updateEditItem}
          onRemoveItem={removeEditItem}
          onTaxRateChange={handleEditTaxRateChange}
        />

        <InvoiceDetailDialog
          invoice={viewingInvoice}
          open={!!viewingInvoice}
          onOpenChange={() => setViewingInvoice(null)}
          t={t}
          companyInfo={companyInfo}
          client={viewingInvoice ? getClient(viewingInvoice.client_id) : null}
          getClientName={getClientName}
          getClientRUC={getClientRUC}
          onIssue={() =>
            viewingInvoice && requestInvoiceIssue(
              viewingInvoice,
              { status: 'emitida' },
              { closeViewing: true }
            )
          }
          onMarkPaid={() =>
            viewingInvoice && handleStatusChange(viewingInvoice._id, 'pagada')
          }
          onEdit={() => {
            if (!viewingInvoice) return;
            openEditDialog(viewingInvoice);
            setViewingInvoice(null);
          }}
          onExportPdf={exportInvoicePdf}
        />

        <AppDialog
          open={cufeDialogOpen}
          onOpenChange={(open) => {
            setCufeDialogOpen(open);
            if (!open) {
              setPendingIssueRequest(null);
              setCufeValue('');
              setCufeError('');
            }
          }}
          size="sm"
          title="CUFE requerido"
        >
          <div className="space-y-4">
            <p className="text-sm text-[#9CA3AF]">
              Esta factura tiene impuesto mayor a 0%. Ingresa el CUFE de la DGI
              antes de emitirla.
            </p>
            <Input
              value={cufeValue}
              onChange={(event) => {
                setCufeValue(event.target.value);
                setCufeError('');
              }}
              className="valhalla-input font-mono"
              placeholder="CUFE de la factura"
              autoFocus
            />
            {cufeError ? (
              <p className="text-sm text-red-400">{cufeError}</p>
            ) : null}
            <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
              <ActionButton
                label="Cancelar"
                tone="neutral"
                onClick={() => {
                  setCufeDialogOpen(false);
                  setPendingIssueRequest(null);
                  setCufeValue('');
                  setCufeError('');
                }}
                disabled={cufeSubmitting}
              />
              <ActionButton
                label={cufeSubmitting ? "Emitiendo..." : "Confirmar emisión"}
                tone="primary"
                onClick={confirmCufeIssue}
                disabled={cufeSubmitting}
              />
            </div>
          </div>
        </AppDialog>

      </div>
    </Layout>
  );
}

export default InvoicesPage;
